// ===========================================================================
// Shared sign-in / create-account form. Used by the account panel, the billing
// panel and the Lumi Plus page sidebar. Creating an account is adult-only;
// SIGNING IN also opens a NAMED KEEPSAKE (server keepsake-auth.js), which a
// teen can have and an adult may have instead, without the form saying which.
//
// Age verification is PART of account creation, not a prerequisite the user must
// go do elsewhere. On "Create account" the form gets an age token automatically:
// it reuses a stored proof if there is one, accepts a self-declared token in dev,
// and otherwise runs the selfie verification right here in the form -- the camera
// panel only appears if that selfie step is actually needed. Calls onDone(account)
// on success. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el, toast } from '../dom.js';
import { getFingerprint } from '../device-signal.js';
import { ensureAgeToken, decodeAgeToken, clearAge } from '../age.js';
import { SERVER_HTTP } from '../config.js';
import { loginKeepsake, recoverKeepsake } from '../keepsake.js';
import { renderRecoveryCode } from './recovery-code.js';

export function renderAuth(account, onDone, opts = {}) {
  let mode = opts.mode === 'login' ? 'login' : 'register';
  let cancelled = false;
  // The band this browser is in, when known. A teen (13-17) cannot have an
  // account, so for them the form is the door to a named keepsake and
  // nothing else; for an adult the account is tried first and the keepsake
  // after it, and the person never learns which they have.
  const band = opts.band === 'minor' ? 'minor' : opts.band === 'adult' ? 'adult' : null;

  // A placeholder is not a name: it goes away the moment somebody types,
  // and several screen readers never announce it. Every field carries an
  // aria-label, and the autocomplete that tells a password manager what it
  // is looking at.
  const username = el('input', { type: 'text', placeholder: t('Username'), maxlength: '24', autocomplete: 'username', aria: { label: 'Username' } });
  const password = el('input', { type: 'password', placeholder: t('Password (8+ characters)'), autocomplete: 'current-password', aria: { label: 'Password' } });
  const year = el('input', { type: 'number', placeholder: t('Birth year'), min: '1900', max: '2026', autocomplete: 'bday-year', aria: { label: 'Birth year' } });
  const err = el('div', { class: 'error' });
  const tabs = el('div', { class: 'auth-tabs' });
  const claimMsg = el('div', { class: 'claim-msg' });
  const yearWrap = el('div', { class: 'year-wrap' });
  const submitBtn = el('button', { class: 'btn btn-primary' });

  const formEl = el('div', { class: 'auth-fields' }, [tabs, claimMsg, username, password, yearWrap, err, submitBtn]);

  // Verify panel: built once, revealed only if the selfie step actually runs.
  const verifyVideo = el('video', { autoplay: '', playsinline: '', muted: '', class: 'verify-video' });
  // These two change on their own while the check runs, so they announce
  // themselves. This is what aria-live on #app was reaching for, without
  // making every other change on the site an announcement too.
  const verifyTitle = el('div', { class: 'verify-title', role: 'status' });
  const verifySub = el('div', { class: 'verify-sub', role: 'status' });
  const verifyEl = el('div', { class: 'verify-panel hidden' }, [
    // This has to match what the server actually does. It used to say the photo
    // was "deleted, and never stored", which stopped being true the moment
    // checks were kept so the age model could be audited -- and a promise that
    // is not kept is worse than one never made.
    el('p', {
      class: 'side-sub',
      text:
        'We confirm your age with a quick selfie. It is never shown to anyone you chat with. ' +
        'A moderator may review it, and it is kept for up to 90 days so we can check our age ' +
        'checking is working, then deleted. You only do this once a day.',
    }),
    verifyVideo,
    verifyTitle,
    verifySub,
    el('button', { class: 'btn btn-ghost btn-sm', text: t('Cancel'), onClick: () => { cancelled = true; doneVerify(); } }),
  ]);
  // Where the recovery flow renders. Kept beside the form rather than inside
  // it so switching between them cannot disturb the form's own state.
  const panelWrap = el('div', {});
  const forgotBtn = el('button', {
    class: 'btn btn-ghost btn-sm auth-forgot',
    text: t('Forgot your password?'),
    onClick: () => showRecoverForm(),
  });
  const forgotWrap = el('div', {});
  const wrap = el('div', { class: 'auth-form' }, [formEl, forgotWrap, verifyEl, panelWrap]);

  let verifyShown = false;
  function showPanel() {
    if (verifyShown) return;
    verifyShown = true;
    formEl.classList.add('hidden');
    verifyEl.classList.remove('hidden');
  }
  function doneVerify(errorMsg) {
    verifyShown = false;
    verifyEl.classList.add('hidden');
    formEl.classList.remove('hidden');
    submitBtn.disabled = false;
    submitBtn.textContent = mode === 'login' ? t('Sign in') : t('Create account (18+)');
    if (errorMsg) err.textContent = errorMsg;
  }

  const verifyUi = {
    onVideo: (s) => {
      showPanel();
      verifyVideo.srcObject = s;
      verifyVideo.play?.().catch(() => {});
    },
    onStatus: (titleText, s) => {
      showPanel();
      verifyTitle.textContent = titleText;
      verifySub.textContent = s || '';
    },
    isCancelled: () => cancelled,
  };

  // Signing in to a named keepsake, with the same two fields (keepsake.js).
  // True when it answered -- signed in, or refused with a reason of its own
  // to show -- and false when it simply did not know the name, which leaves
  // the account's own "wrong username or password".
  const keepsakeSignIn = async () => {
    const { status, data } = await loginKeepsake({ username: username.value.trim(), password: password.value, base: SERVER_HTTP });
    if (status === 200) {
      toast('Signed in.');
      onDone?.(account, { keepsake: data });
      return true;
    }
    if (status === 403 && data?.error === 'band_mismatch') {
      err.textContent = 'That name was kept in the other age group, so it cannot be used here.';
      return true;
    }
    if (status === 429 && data?.message) {
      err.textContent = data.message;
      return true;
    }
    return false;
  };

  const doLogin = async () => {
    // Disabled while a request is in flight, so a second click (or a click on a
    // button already working) cannot send a second sign-in.
    if (submitBtn.disabled) return;
    err.textContent = '';
    submitBtn.disabled = true;
    try {
      const fingerprint = await getFingerprint().catch(() => '');
      // A teen has no account to try: their name is on their keepsake.
      const viaAccount = band !== 'minor';
      let { status, data } = viaAccount
        ? await account.login(username.value.trim(), password.value, fingerprint)
        : { status: 401, data: {} };
      // A short 429 (retryAfter of two seconds or less) is the busy gate or the
      // one-at-a-time turn, not a rate limit: wait it out once and try again
      // before showing anything, so a moment's contention is invisible.
      if (status === 429 && Number(data?.retryAfter) > 0 && Number(data.retryAfter) <= 2) {
        await new Promise((resolve) => setTimeout(resolve, Number(data.retryAfter) * 1000));
        ({ status, data } = await account.login(username.value.trim(), password.value, fingerprint));
      }
      // A named keepsake answers to the same two fields (keepsake.js): tried
      // once the account has said no, or instead of it in the teen band, so
      // the person never has to say which kind of identity they have.
      if (status === 401 && (await keepsakeSignIn())) return;
      if (status === 200) {
        toast('Signed in.');
        onDone?.(account);
      } else if (status === 409 && data.error === 'claim_required') {
        // An admin pre-granted this username -> finish setup by choosing a password.
        mode = 'claim';
        syncMode();
      } else if (status === 429 && data?.message) {
        // A real rate limit: the server says why, in its own words.
        err.textContent = data.message;
      } else {
        err.textContent = 'Wrong username or password.';
      }
    } finally {
      submitBtn.disabled = false;
    }
  };

  // The age step both forms share: an adult proof for this device and the
  // fingerprint it was got with, or null once the person has been told why
  // not, in the words each form already used.
  const adultProof = async (by, underAgeText) => {
    let fingerprint;
    let token;
    try {
      fingerprint = await getFingerprint();
      token = await ensureAgeToken(by, fingerprint, verifyUi);
    } catch (e) {
      if (String(e?.message) === 'cancelled') doneVerify();
      else doneVerify(e?.message || 'Age check could not be completed.');
      return null;
    }
    if (decodeAgeToken(token)?.band !== 'adult') {
      doneVerify(underAgeText);
      return null;
    }
    return { token, fingerprint };
  };

  const doClaim = async () => {
    err.textContent = '';
    if (password.value.length < 8) {
      err.textContent = 'Choose a password of at least 8 characters.';
      return;
    }
    const by = Number(year.value);
    if (!by) {
      err.textContent = 'Enter your birth year to confirm you are 18+.';
      return;
    }
    cancelled = false;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Confirming your age...';
    const proof = await adultProof(by, 'You must be 18 or older to claim Lumi Plus.');
    if (!proof) return;
    let { status, data } = await account.claim(username.value.trim(), password.value, proof.token, proof.fingerprint);
    if (status === 403 && data.error === 'adult_only') {
      // A proof that reads adult and is still not this device's to use (bound
      // to a keepsake revoked since, say): dropped, and asked for once more.
      clearAge();
      const again = await adultProof(by, 'You must be 18 or older to claim Lumi Plus.');
      if (!again) return;
      ({ status, data } = await account.claim(username.value.trim(), password.value, again.token, again.fingerprint));
    }
    if (status === 200) {
      toast('Welcome to Lumi Plus!');
      onDone?.(account);
    } else {
      doneVerify(
        data.error === 'already_claimed'
          ? 'This account is already set up: just sign in.'
          : data.error === 'adult_only'
            ? 'Lumi Plus accounts are 18+ only.'
            : 'Could not finish setting up your account.',
      );
    }
  };

  const doRegister = async () => {
    err.textContent = '';
    if (!/^[a-z0-9_.-]{3,24}$/i.test(username.value.trim())) {
      err.textContent = 'Pick a username (3-24 letters, digits, _ . -).';
      return;
    }
    if (password.value.length < 8) {
      err.textContent = 'Password must be at least 8 characters.';
      return;
    }
    const by = Number(year.value);
    if (!by) {
      err.textContent = 'Enter your birth year (you must be 18+).';
      return;
    }
    cancelled = false;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Confirming your age...';
    const proof = await adultProof(by, 'You must be 18 or older to create an account.');
    if (!proof) return;
    let { status, data } = await account.register(username.value.trim(), password.value, proof.token, proof.fingerprint);
    if (status === 403 && data.error === 'adult_only') {
      // As claim: a proof the server will not take here is asked for once more.
      clearAge();
      const again = await adultProof(by, 'You must be 18 or older to create an account.');
      if (!again) return;
      ({ status, data } = await account.register(username.value.trim(), password.value, again.token, again.fingerprint));
    }
    if (status === 200) {
      // The one moment this code exists in readable form. There is no email on
      // a Lumi account, so if they walk past this screen without keeping it,
      // a forgotten password means asking a human who cannot verify them.
      // Hence a step they have to acknowledge rather than a toast they can miss.
      if (data.recoveryCode) showRecoveryCode(data.recoveryCode, () => onDone?.(account));
      else {
        toast('Account created.');
        onDone?.(account);
      }
    } else {
      doneVerify(
        data.error === 'username_taken'
          ? 'That username is taken.'
          : data.error === 'weak_password'
            ? 'Password must be at least 8 characters.'
            : data.error === 'adult_only'
              ? 'Accounts are 18+ only.'
              : 'Could not create the account.',
      );
    }
  };

  function syncMode() {
    if (mode === 'claim') {
      tabs.replaceChildren();
      claimMsg.textContent =
        'An admin set you up with Lumi Plus! Choose a password to finish: you will use it to sign in from now on.';
      username.disabled = true;
      password.placeholder = 'Choose a password (8+ characters)';
      password.autocomplete = 'new-password';
      yearWrap.replaceChildren(year);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Claim Lumi Plus';
      submitBtn.onclick = doClaim;
      err.textContent = '';
      return;
    }
    username.disabled = false;
    claimMsg.textContent = '';
    password.placeholder = 'Password (8+ characters)';
    // No "Create account" for a teen: accounts are 18+, and what a teen can
    // keep is offered where their keepsake is, in the account panel.
    if (band === 'minor') mode = 'login';
    tabs.replaceChildren(
      el('button', { class: `tab ${mode === 'login' ? 'on' : ''}`, text: t('Sign in'), onClick: () => { mode = 'login'; syncMode(); } }),
      ...(band === 'minor' ? [] : [el('button', { class: `tab ${mode === 'register' ? 'on' : ''}`, text: t('Create account'), onClick: () => { mode = 'register'; syncMode(); } })]),
    );
    yearWrap.replaceChildren(...(mode === 'register' ? [year] : []));
    password.autocomplete = mode === 'register' ? 'new-password' : 'current-password';
    submitBtn.disabled = false;
    submitBtn.textContent = mode === 'login' ? t('Sign in') : t('Create account (18+)');
    submitBtn.onclick = mode === 'login' ? doLogin : doRegister;
    // Only offered on the sign-in tab: it is the only place somebody arrives
    // already knowing they cannot get in.
    forgotWrap.replaceChildren(...(mode === 'login' ? [forgotBtn] : []));
    err.textContent = '';
  }

  // ---- The recovery code, shown exactly once --------------------------------
  //
  // A step, not a notice. There is no email on a Lumi account and only a hash of
  // this code is kept, so nobody -- including us -- can produce it again. If it
  // is not written down here, a forgotten password later means asking an admin
  // who has no way to prove who is asking.
  // The step itself is ui/recovery-code.js, shared with the account panel's
  // "keep this identity"; `what` names what the code is the way back into.
  function showRecoveryCode(code, done, what = 'your account') {
    wrap.append(renderRecoveryCode(code, done, { what }));
  }

  // ---- Lost the password ----------------------------------------------------
  function showRecoverForm() {
    let attemptsLeft = null;
    const u = el('input', { type: 'text', class: 'auth-input', placeholder: t('Username'), autocomplete: 'username', aria: { label: 'Username' } });
    u.value = username.value.trim();
    const c = el('input', { type: 'text', class: 'auth-input', placeholder: t('Recovery code from signup'), autocomplete: 'off', spellcheck: 'false', aria: { label: 'Recovery code from signup' } });
    const pw = el('input', { type: 'password', class: 'auth-input', placeholder: t('New password (8+ characters)'), autocomplete: 'new-password', aria: { label: 'New password' } });
    const msg = el('div', { class: 'auth-err' });
    const askBtn = el('button', { class: 'btn btn-ghost btn-sm', text: t('Report to an admin') });
    const askWrap = el('div', {});
    const submit = el('button', { class: 'btn btn-primary', text: t('Set a new password') });

    askBtn.onclick = async () => {
      askBtn.disabled = true;
      await account.recoveryHelp(u.value.trim(), await getFingerprint().catch(() => ''));
      // Deliberately says nothing about whether the account exists. A real
      // person loses nothing by this; somebody fishing for usernames learns
      // nothing from it.
      askWrap.replaceChildren(el('p', { class: 'muted', text:
        'Sent. Open the Support tab and keep the page open, and an admin will reply there. '
        + 'They cannot prove who you are, so they may ask you things only you would know.' }));
    };

    submit.onclick = async () => {
      msg.textContent = '';
      submit.disabled = true;
      // A teen has no account: the code they hold is their keepsake's.
      const { status, data } = band === 'minor'
        ? { status: 403, data: {} }
        : await account.recover(u.value.trim(), c.value.trim(), pw.value, await getFingerprint().catch(() => ''));
      submit.disabled = false;
      if (status === 200) {
        if (data.recoveryCode) showRecoveryCode(data.recoveryCode, () => onDone?.(account));
        else onDone?.(account);
        return;
      }
      if (data.error === 'weak_password') {
        msg.textContent = 'Password must be at least 8 characters.';
        return;
      }
      // The same code against a named keepsake (keepsake.js), before this is
      // called a wrong code: the person does not know which kind they hold.
      submit.disabled = true;
      const ks = await recoverKeepsake({ username: u.value.trim(), code: c.value.trim(), password: pw.value, base: SERVER_HTTP });
      submit.disabled = false;
      if (ks.status === 200) {
        const done = () => onDone?.(account, { keepsake: ks.data });
        if (ks.data.recoveryCode) showRecoveryCode(ks.data.recoveryCode, done, 'this identity');
        else done();
        return;
      }
      if (ks.status === 403 && ks.data?.error === 'band_mismatch') {
        msg.textContent = 'That name was kept in the other age group, so it cannot be used here.';
        return;
      }
      attemptsLeft = typeof data.attemptsLeft === 'number' ? data.attemptsLeft : attemptsLeft;
      if (data.exhausted || attemptsLeft === 0) {
        // Not a security boundary -- the code has far too much entropy to guess.
        // This is the point where somebody who has genuinely lost it should be
        // handed a person rather than left trying the same thing.
        msg.textContent = 'That code was not right, and there are no more tries.';
        c.disabled = true;
        submit.disabled = true;
        askWrap.replaceChildren(
          el('p', { class: 'muted', text: t('If you have lost the code as well, an admin can look at it.') }),
          askBtn,
        );
        return;
      }
      msg.textContent = attemptsLeft === null
        ? 'That did not work.'
        : `That code was not right. ${attemptsLeft} ${attemptsLeft === 1 ? 'try' : 'tries'} left.`;
    };

    panelWrap.replaceChildren(
      el('h3', { text: t('Use your recovery code') }),
      el('p', { class: 'muted', text: t('The code you were given when you created the account.') }),
      u, c, pw, msg, submit, askWrap,
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Back to sign in'), onClick: () => { panelWrap.replaceChildren(); formEl.hidden = false; } }),
    );
    formEl.hidden = true;
  }

  syncMode();
  return wrap;
}

export default { renderAuth };
